import React, { useEffect, useState } from "react";
import { supabase } from "../../supabaseClient";

export default function TeamResources() {
  const [resources, setResources] = useState([]);
  const [selectedResource, setSelectedResource] = useState(null); // For viewing
  const [editResource, setEditResource] = useState(null); // For create/edit modal
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const teamId = "02296ed6-981c-44a7-9301-41b07becf883";

  // Fetch resources
  useEffect(() => {
    const fetchResources = async () => {
      setLoading(true);
      const { data, error } = await supabase
        .from("team_resources")
        .select("*")
        .eq("team_id", teamId)
        .order("created_at", { ascending: false });

      if (error) console.error("Error fetching resources:", error);
      else setResources(data || []);
      setLoading(false);
    };

    if (teamId) fetchResources();
  }, [teamId]);

  // Save or update resource
  const handleSaveResource = async (resource) => {
    try {
      setSaving(true);
      if (resource.id) {
        // Update existing resource
        const { error } = await supabase
          .from("team_resources")
          .update({
            resource_name: resource.resource_name,
            resource_notes: resource.resource_notes,
            resource_images: resource.resource_images,
            resource_video: resource.resource_video
          })
          .eq("id", resource.id);

        if (error) throw error;
      } else {
        // Insert new resource
        const { error } = await supabase
          .from("team_resources")
          .insert([{ ...resource, team_id: teamId }]);

        if (error) throw error;
      }

      // Refresh resources
      const { data } = await supabase
        .from("team_resources")
        .select("*")
        .eq("team_id", teamId)
        .order("created_at", { ascending: false });

      setResources(data || []);
      setEditResource(null);
    } catch (err) {
      console.error("Error saving resource:", err.message);
      alert("Failed to save resource.");
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <p>Loading resources...</p>;

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-semibold text-gray-800">Team Resources</h1>
        <button
          className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg"
          onClick={() => setEditResource({})} // empty object for new resource
        >
          + New Resource
        </button>
      </div>

      {resources.length === 0 ? (
        <p className="text-gray-500">No resources found for this team.</p>
      ) : (
        <div className="grid gap-6 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3">
          {resources.map((resource) => (
            <div
              key={resource.id}
              className="bg-white rounded-xl shadow hover:shadow-lg p-4 transition cursor-pointer border border-gray-100"
            >
              <h2
                className="font-semibold text-lg text-gray-900 mb-2"
                onClick={() => setSelectedResource(resource)}
              >
                {resource.resource_name}
              </h2>
              <p className="text-sm text-gray-600 line-clamp-2">
                {resource.resource_notes}
              </p>
              <button
                className="mt-2 text-blue-600 hover:underline text-sm"
                onClick={() => setEditResource(resource)}
              >
                Edit
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Modal for viewing resource */}
      {selectedResource && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex justify-center items-center z-50">
          <div className="bg-white rounded-xl shadow-lg max-w-lg w-full p-6 relative">
            <button
              onClick={() => setSelectedResource(null)}
              className="absolute top-2 right-3 text-gray-500 hover:text-gray-700"
            >
              ✕
            </button>

            <h2 className="text-2xl font-semibold text-gray-900 mb-3">
              {selectedResource.resource_name}
            </h2>

            {selectedResource.resource_images && (
              <img
                src={selectedResource.resource_images}
                alt="Resource"
                className="w-full rounded-lg mb-3"
              />
            )}

            {selectedResource.resource_video && (
              <video
                src={selectedResource.resource_video}
                controls
                className="w-full rounded-lg mb-3"
              />
            )}

            <p className="text-gray-700 whitespace-pre-wrap">
              {selectedResource.resource_notes}
            </p>

            <p className="text-sm text-gray-400 mt-4">
              Created at:{" "}
              {new Date(selectedResource.created_at).toLocaleDateString()}
            </p>
          </div>
        </div>
      )}

      {/* Modal for create/edit */}
      {editResource && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex justify-center items-center z-50">
          <div className="bg-white rounded-xl shadow-lg max-w-lg w-full p-6 relative">
            <button
              onClick={() => setEditResource(null)}
              className="absolute top-2 right-3 text-gray-500 hover:text-gray-700"
            >
              ✕
            </button>

            <h2 className="text-2xl font-semibold text-gray-900 mb-4">
              {editResource.id ? "Edit Resource" : "New Resource"}
            </h2>

            <form
              onSubmit={(e) => {
                e.preventDefault();
                handleSaveResource(editResource);
              }}
            >
              <input
                type="text"
                placeholder="Resource Name"
                value={editResource.resource_name || ""}
                onChange={(e) =>
                  setEditResource((prev) => ({
                    ...prev,
                    resource_name: e.target.value
                  }))
                }
                className="border w-full p-2 rounded mb-3"
                required
              />
              <textarea
                placeholder="Resource Notes"
                value={editResource.resource_notes || ""}
                onChange={(e) =>
                  setEditResource((prev) => ({
                    ...prev,
                    resource_notes: e.target.value
                  }))
                }
                className="border w-full p-2 rounded mb-3"
                rows={4}
              />
              <input
                type="text"
                placeholder="Image URL"
                value={editResource.resource_images || ""}
                onChange={(e) =>
                  setEditResource((prev) => ({
                    ...prev,
                    resource_images: e.target.value
                  }))
                }
                className="border w-full p-2 rounded mb-3"
              />
              <input
                type="text"
                placeholder="Video URL"
                value={editResource.resource_video || ""}
                onChange={(e) =>
                  setEditResource((prev) => ({
                    ...prev,
                    resource_video: e.target.value
                  }))
                }
                className="border w-full p-2 rounded mb-3"
              />
              <button
                type="submit"
                disabled={saving}
                className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg"
              >
                {saving ? "Saving..." : "Save Resource"}
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
